/* eslint-disable max-lines */
import BottomSheetModal from '@gorhom/bottom-sheet/src/components/bottomSheetModal';
import {
  Address,
  AddressInput,
  AddressValidationType,
  ChannelType,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { getDcfButtonAnalytics } from 'features/moveMoney/screens/dcf/helpers/DcfAnalytics.helpers';
import { deferOnWeb } from 'features/moveMoney/screens/dcf/helpers/useDeferOnWeb';
import { useTabaPayDebitCardFundingForm } from 'features/moveMoney/screens/dcf/hooks/useTabaPayDebitCardFundingForm';
import useTabapayTransfer from 'features/moveMoney/screens/dcf/hooks/useTabaPayTransfer';
import useTransferLimitations from 'features/moveMoney/screens/dcf/hooks/useTransferLimitations';
import {
  getExternalAddressText,
  getFlowNameForAnalytics,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import TabapayTransferErrorScreen from 'features/moveMoney/screens/dcf/tabapay/TabapayTransfer/components/TabapayTransferErrorScreen';
import { useTabapayErrorHandlers } from 'features/moveMoney/screens/dcf/tabapay/TabapayTransfer/hooks/useTabapayErrorHandlers';
import { formatExpDate } from 'features/v2a/receiver/helpers/V2A.helpers';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { VaroPressableNames } from 'shared/analytics/types/pressableNames';
import AmountInput, {
  AmountInputRef,
} from 'shared/components/vxl/AmountInput/AmountInput';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import VXLDropdown, {
  VXL_DROPDOWN_MENU_RESERVED_SPACE,
} from 'shared/components/vxl/input/VXLDropdown/VXLDropdown';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Pill from 'shared/components/vxl/navigation/Pill/Pill';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isMobileOS, isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useGetProfileInfo from 'shared/hooks/useGetProfileInfo/useGetProfileInfo';
import useValidateAddress, {
  AddressView,
} from 'shared/hooks/useValidateAddress/useValidateAddress';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { isValidCardNumber } from 'shared/utils/string/cardNumber';
import { filterDigits } from 'shared/utils/string/string.helpers';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

const TabapayTransferScreenV2 = () => {
  const router = useRouter();
  const scrollViewRef = useRef(null);
  const addressValidationBottomSheetRef = useRef<BottomSheetModal>(null);
  const {
    tabaPayEntryFlowType = TabaPayEntryFlowType.ExternalAccount,
    isBelieveDay0Flow,
  } = useLocalSearchParams<{
    tabaPayEntryFlowType?: TabaPayEntryFlowType;
    isBelieveDay0Flow?: string;
  }>();
  const { bottom } = useSafeAreaInsets();
  const { t: translatedText2 } = useTranslation('moveMoney', {
    keyPrefix: 'dcf',
  });

  const [pillAmount, setPillAmount] = useState<string>();
  const [isCvvVisible, setCvvVisible] = useState(false);
  const [userAddress, setUserAddress] = useState<Address>();
  const [userEnteredAddress, setUserEnteredAddress] = useState<AddressInput>();
  const cardNumberInputRef = useRef<TextInput | null>(null);
  const dateInputRef = useRef<TextInput | null>(null);
  const cvvInputRef = useRef<TextInput | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const {
    combinedErrorScreen,
    addNewAddressOnPressEvent,
    control,
    cancelTransferLoading,
    shouldShowLoader,
    setShouldRefreshCards,
    translatedText,
    handleErrorButtonPress,
    handleErrorPrimaryButtonOnPress,
    errors,
    selectedAddress,
    finalDropdownAddressList,
    setSelectedAddress,
    handleSubmitAction: handleFormSubmit,
    isValid,
    cancelTabapayTransfer,
    transactionId,
    errorScreenWeb,
    errorScreenThreeDs,
    handleCardNumberInputErrorTriggered,
    handleExpiryDateErrorTriggered,
    handleCvvInputErrorTriggered,
    trigger,
    checkAndDeleteCard,
    externalCardId,
  } = useTabaPayDebitCardFundingForm();

  const flow = useVaroOnboardingFlow();

  // Forwards the resolved amount through router.replace after partial-auth X.
  // The destination seeds its transfer amount from partialAuthAmount.
  // Its presence preserves first-vs-subsequent state across remounts.
  const handlePartialAuthCancelSuccess = (nextAmount?: string) => {
    if (!flow) {
      return;
    }

    deferOnWeb(() => {
      router.replace({
        pathname: `/${flow}/tabapay/tabapay-transfer-screen`,
        params: { partialAuthAmount: nextAmount ?? '0.00' },
      });
    });
  };

  const {
    transferAmount,
    handleSubmit: handlePaymentSubmit,
    bottomSheetRef,
    handleErrorButtonPress: handleTransferErrorButtonPress,
    combinedErrorScreen2,
    combinedLoading,
    transactionId: transactionId2,
    cancelTabapayTransfer: cancelTabapayTransfer2,
    errorThreeDsWeb,
    errorThreeDsHandler,
    sourceName,
    checkAndDeleteCard: checkAndDeleteCard2,
    isEligible,
    eligibleErrorScreen,
    limitErrorMessage,
    maxAmount,
    minAmount,
    tabaPayLimitMessageData,
    setTransferAmount,
    isProcessing,
    markPartialAuthClose,
  } = useTabapayTransfer({
    onPartialAuthCancelSuccess: handlePartialAuthCancelSuccess,
  });
  const amountRef = useRef<AmountInputRef>(null);

  const [isAmountTouched, setIsAmountTouched] = useState(false);

  const otherPillTitle = tabaPayLimitMessageData?.options?.find(({ title }) =>
    [translatedText2('otherAmount'), translatedText2('other')].includes(title),
  )?.title;

  const handleAmountBlur = () => {
    if (pillAmount === otherPillTitle) {
      setIsAmountTouched(true);
    }
  };

  const {
    isSubmitDisabled: isSubmitButtonDisabled,
    transferLimitations,
    transferLimitationsIcon,
    transferLimitationsTextColor,
  } = useTransferLimitations({
    transferAmount,
    maxAmount,
    minAmount,
    limitErrorMessage,
  });

  const showAmountCaption = isAmountTouched || Number(transferAmount) > 0;

  const flowName = getFlowNameForAnalytics(TabaPayEntryFlowType.DCFFlow);
  const isBD0Flow = isBelieveDay0Flow === 'true';

  useEffect(() => {
    if (externalCardId) {
      void handlePaymentSubmit(externalCardId);
    }
  }, [externalCardId]);

  const errorScreenConfig = useTabapayErrorHandlers({
    cardFormError: {
      combinedErrorScreen,
      errorScreenWeb,
      errorScreenThreeDs,
      setShouldRefreshCards,
      cancelTabapayTransfer,
      transactionId,
      checkAndDeleteCard,
      translatedText,
      handleErrorPrimaryButtonOnPress,
      handleErrorButtonPress,
      cancelTransferLoading,
    },
    transferError: {
      combinedErrorScreen: combinedErrorScreen2,
      errorThreeDsWeb,
      errorThreeDsHandler,
      cancelTabapayTransfer: cancelTabapayTransfer2,
      transactionId: transactionId2,
      checkAndDeleteCard: checkAndDeleteCard2,
      sourceName,
      translatedText: translatedText2,
      handleErrorButtonPress: handleTransferErrorButtonPress,
      onPartialAuthClose: markPartialAuthClose,
    },
    eligibility: {
      isEligible,
      eligibleErrorScreen,
    },
    flowName,
    isProcessing,
  });

  useLayoutEffect(() => {
    if (!shouldShowLoader && !combinedLoading) {
      setInitialLoading(false);
    }
  }, [shouldShowLoader, combinedLoading, errorScreenConfig]);

  const { result } = useGetProfileInfo();

  const { addressVerification, resetAddressVerification } = useValidateAddress(
    userEnteredAddress!,
    ChannelType.Customer,
    AddressValidationType.ExternalAddress,
  );
  const hasSetAddressRef = useRef(false);

  useEffect(() => {
    const addressToSet = result?.customerInfo?.mailingAddress?.addressLine1
      ? result.customerInfo.mailingAddress
      : result?.customerInfo?.address;
    if (
      !hasSetAddressRef.current &&
      addressToSet &&
      addressVerification?.addressType !== AddressView.DefaultAddress
    ) {
      setUserAddress(addressToSet);
      hasSetAddressRef.current = true;
    }
  }, [addressVerification, result]);

  const handlePillAction = (title: string) => {
    let amount: string;
    const isOther = [
      translatedText2('otherAmount'),
      translatedText2('other'),
    ].includes(title);
    if (isOther) {
      amount = '0.00';
      amountRef.current?.focus();
      setIsAmountTouched(true);
    } else {
      amountRef.current?.blur();
      amount = title.replace(translatedText2('currency'), '');
      setIsAmountTouched(false);
    }
    setTransferAmount(amount);
    setPillAmount(title);
  };

  const selectOtherPill = () => {
    if (otherPillTitle) {
      setPillAmount(otherPillTitle);
    }
  };
  const handleAmountChange = (amount: string) => {
    selectOtherPill();
    setTransferAmount(amount);
    setIsAmountTouched(true);
  };

  useEffect(() => {
    if (!addressVerification) {
      return;
    }

    const { addressType, fullAddress } = addressVerification;

    if (
      addressType === undefined ||
      addressType === AddressView.DefaultAddress
    ) {
      return;
    }

    if (userAddress !== result?.customerInfo.address) {
      newAddressEntered();
    }
    if (addressType !== AddressView.FormAddress && fullAddress) {
      addressValidationBottomSheetRef.current?.present();
      return;
    }

    if (addressType === AddressView.FormAddress) {
      addressValidationBottomSheetRef.current?.dismiss();
      bottomSheetRef.current?.dismiss();
      setUserAddress(fullAddress?.originalAddress);
      setUserEnteredAddress(undefined);
      resetAddressVerification();
    }
  }, [addressVerification, resetAddressVerification]);

  const newAddressEntered = usePressableAnalytics({
    analyticsId: 'newAddressEntered',
    customAttributes: {
      smartyStreetResponse: addressVerification?.addressType,
    },
  });

  const errorComponent = errorScreenConfig ? (
    <TabapayTransferErrorScreen {...errorScreenConfig} />
  ) : null;

  const shouldShowBackButton =
    isMobileOS || (!errorScreenConfig && !shouldShowLoader && !combinedLoading);

  const shouldShowHeader = isWeb
    ? shouldShowBackButton && !errorScreenConfig
    : shouldShowBackButton;

  const isOverlayLoading =
    initialLoading ||
    isProcessing ||
    (!combinedErrorScreen &&
      !combinedErrorScreen2 &&
      !eligibleErrorScreen &&
      (shouldShowLoader || combinedLoading));

  return (
    <>
      <Stack.Screen
        options={{
          title: translatedText2('title'),
          headerShown: shouldShowHeader,
          headerBackVisible: shouldShowHeader,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={isOverlayLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={errorComponent}
      />
      <View style={[{ paddingBottom: bottom }, styles.containerView]}>
        <View style={styles.container}>
          <ScrollView
            ref={scrollViewRef}
            keyboardShouldPersistTaps="always"
            automaticallyAdjustKeyboardInsets
            style={styles.scrollContent}
            contentContainerStyle={[
              styles.scrollContentContainer,
              styles.addressDropdownBottomPadding,
            ]}
          >
            <View style={styles.transferAmountContainer}>
              <AmountInput
                ref={amountRef}
                value={transferAmount}
                captionText={
                  showAmountCaption ? transferLimitations : undefined
                }
                onChangeText={handleAmountChange}
                onBlur={handleAmountBlur}
                prefix="$"
                captionTextIcon={
                  showAmountCaption ? transferLimitationsIcon : undefined
                }
                captionTextColor={
                  showAmountCaption ? transferLimitationsTextColor : undefined
                }
                isDisabled={Boolean(limitErrorMessage)}
              />
            </View>
            <View style={styles.pillContainer}>
              {tabaPayLimitMessageData?.options
                ? tabaPayLimitMessageData.options.map(({ id, title }) => (
                    <Pill
                      key={id}
                      labelText={title}
                      onPress={() => handlePillAction(title)}
                      isSelected={pillAmount === title}
                      analyticsId="pillsTabapayClicked"
                      customAttributes={{ transfer_amount: title }}
                    />
                  ))
                : null}
            </View>
            <View
              style={styles.formContainer}
              pointerEvents="box-none"
              collapsable={false}
            >
              <View
                style={styles.personalInfoFormContainer}
                collapsable={false}
                pointerEvents="box-none"
              >
                <Controller
                  control={control}
                  name="cardNumber"
                  render={({ field: { onChange, onBlur, value } }) => {
                    const handleCardNumberChange = (text: string) => {
                      const cleaned = text.replace(/[.-]/g, '');
                      const digits = cleaned.replace(/\D/g, '');
                      const formatted = digits.replace(/(.{4})/g, '$1 ').trim();
                      onChange(formatted);
                      void trigger('cardNumber');
                      if (isValidCardNumber(digits)) {
                        dateInputRef.current?.focus?.();
                      }
                    };

                    return (
                      <View
                        style={
                          tabaPayLimitMessageData?.description
                            ? undefined
                            : styles.bottomPadding
                        }
                      >
                        <VXLTextInput
                          ref={cardNumberInputRef}
                          labelText={translatedText('cardNumber')}
                          onChangeText={handleCardNumberChange}
                          onBlur={() => {
                            onBlur();
                            void trigger('cardNumber');
                            handleCardNumberInputErrorTriggered();
                          }}
                          placeholder={translatedText('cardNumber')}
                          keyboardType="number-pad"
                          returnKeyType="done"
                          value={value}
                          autoCapitalize="none"
                          maxLength={19}
                          errorText={errors.cardNumber?.message ?? ''}
                          onSubmitEditing={() => {
                            dateInputRef.current?.focus?.();
                          }}
                          analyticsId="cardNumberFieldClicked"
                          autoComplete="cc-number"
                          textContentType="creditCardNumber"
                          testID="text-input-cardNumber"
                        />
                      </View>
                    );
                  }}
                />
                {tabaPayLimitMessageData?.description ? (
                  <BodyXSText style={styles.cardDescription}>
                    {tabaPayLimitMessageData.description}
                  </BodyXSText>
                ) : null}
                <View style={styles.formLine}>
                  <Controller
                    control={control}
                    name="date"
                    render={({ field: { onChange, onBlur, value } }) => {
                      const handleChange = async (text: string) => {
                        onChange(text);
                        const isValid = await trigger('date');
                        if (isValid && text.length === 5) {
                          cvvInputRef.current?.focus?.();
                        }
                      };
                      return (
                        <View style={styles.input}>
                          <VXLTextInput
                            ref={dateInputRef}
                            labelText={translatedText('expDate')}
                            errorText={errors.date?.message ?? ''}
                            onChangeText={async (text) => {
                              await handleChange(
                                formatExpDate(text.replace(/\D/g, '')),
                              );
                            }}
                            onBlur={() => {
                              onBlur();
                              void trigger('date');
                              handleExpiryDateErrorTriggered();
                            }}
                            value={value}
                            returnKeyType="done"
                            placeholder={translatedText('dateHint')}
                            keyboardType="number-pad"
                            maxLength={5}
                            autoComplete="cc-exp"
                            textContentType="creditCardExpiration"
                            onSubmitEditing={() => {
                              cvvInputRef.current?.focus?.();
                            }}
                            analyticsId="expDateFieldClicked"
                            testID="text-input-expDate"
                          />
                        </View>
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name="cvv"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <View style={styles.input}>
                        <VXLTextInput
                          ref={cvvInputRef}
                          labelText={translatedText(
                            'tabaPayValidation.securityCode',
                          )}
                          onChangeText={(text) => {
                            const digits = filterDigits(text);
                            onChange(digits);
                            void trigger('cvv');
                          }}
                          inputMode="numeric"
                          maxLength={3}
                          returnKeyType="done"
                          autoComplete="cc-csc"
                          textContentType="creditCardSecurityCode"
                          value={value}
                          placeholder={translatedText(
                            'tabaPayValidation.securityCode',
                          )}
                          autoCapitalize="none"
                          errorText={errors.cvv?.message ?? ''}
                          onBlur={() => {
                            onBlur();
                            void trigger('cvv');
                            handleCvvInputErrorTriggered();
                          }}
                          secureTextEntry={!isCvvVisible}
                          rightIcon={{
                            iconKey: !isCvvVisible ? 'VIEW' : 'HIDE',
                            iconAccessibilityLabel: isCvvVisible
                              ? 'eye-close'
                              : 'eye-open',
                            onPress: () => {
                              setCvvVisible(!isCvvVisible);
                            },
                          }}
                          onSubmitEditing={() => cvvInputRef.current?.blur()}
                          analyticsId="cvvFieldClicked"
                          testID="text-input-tabaPayValidation.securityCode"
                        />
                      </View>
                    )}
                  />
                </View>
                <Controller
                  control={control}
                  name="address"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <View style={styles.billingAddress}>
                      <VXLDropdown
                        labelText={translatedText('address')}
                        placeholder={translatedText('address')}
                        value={value || getExternalAddressText(selectedAddress)}
                        options={[
                          ...finalDropdownAddressList.map(
                            getExternalAddressText,
                          ),
                          translatedText('newAddress'),
                        ]}
                        onChangeText={(text) => {
                          if (text === translatedText('newAddress')) {
                            addNewAddressOnPressEvent();
                            router.push({
                              pathname: `/${flow}/tabapay/address-screen`,
                              params: {
                                tabaPayEntryFlowType,
                                isBelieveDay0Flow,
                              },
                            });
                            return;
                          }
                          const selectedIndex =
                            finalDropdownAddressList.findIndex(
                              (address) =>
                                getExternalAddressText(address) === text,
                            );
                          if (selectedIndex >= 0) {
                            setSelectedAddress(
                              finalDropdownAddressList[selectedIndex],
                            );
                          }
                          onChange(text);
                          onBlur();
                        }}
                        analyticsId="addressFieldClicked"
                        testID="address-select"
                      />
                    </View>
                  )}
                />
              </View>
            </View>
          </ScrollView>

          <View style={styles.stickyBottomContainer}>
            <View style={styles.dividerWrapper}>
              <HairLineDivider />
            </View>
            <View style={styles.buttonContainer}>
              <BodyXSText style={styles.disclaimerInfo}>
                {translatedText2('disclaimerInfo')}
              </BodyXSText>
              <Button
                label={translatedText2('addMoney')}
                onPress={async () => {
                  await handleFormSubmit();
                }}
                isDisabled={!isValid || isSubmitButtonDisabled}
                isLoading={shouldShowLoader || combinedLoading}
                analyticsId="transferMoneyClicked"
                size="LARGE"
                type="PRIMARY"
                width="FULL"
                // The analyticsId above is inert: this screen renders under
                // `/{flow}/tabapay/dcf-tabapay-form`, which has no config.json key.
                componentAnalytics={getDcfButtonAnalytics({
                  pressableName: VaroPressableNames.DCF.DCF_ADD_MONEY_NEW_CARD,
                  isBelieveDay0Flow: isBD0Flow,
                })}
              />
            </View>
          </View>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  addressDropdownBottomPadding: {
    paddingBottom: VXL_DROPDOWN_MENU_RESERVED_SPACE,
  },
  billingAddress: { paddingTop: theme.spacing.m },
  bottomPadding: { paddingBottom: theme.spacing.m },
  cardDescription: {
    color: theme.color.text.secondary,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.m,
  },
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
    marginHorizontal: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  container: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
    minWidth: '100%',
  },
  containerView: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  disclaimerInfo: { color: theme.color.text.secondary },
  dividerWrapper: {
    backgroundColor: theme.color.background.primary,
    paddingBottom: theme.spacing.m,
  },
  formContainer: {
    backgroundColor: theme.color.background.primary,
    paddingTop: theme.spacing.s,
  },
  formLine: {
    flexDirection: 'row',
    gap: theme.spacing.m,
    justifyContent: 'space-between',
  },
  input: {
    flex: 1,
  },
  personalInfoFormContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  pillContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.s,
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  scrollContent: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
  },
  stickyBottomContainer: {
    backgroundColor: theme.color.background.primary,
  },
  transferAmountContainer: {
    height: 140,
    paddingBottom: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
});

export default TabapayTransferScreenV2;
